import { forwardRef, type InputHTMLAttributes } from "react";

import { cn } from "./lib/cn.js";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, id, "aria-describedby": describedBy, ...props }, ref) => {
    // Link the error text to the control so screen readers announce it
    // (assessment U6 — the primitives showed errors visually only).
    const errorId = id ? `${id}-error` : undefined;
    const describedByIds = [describedBy, error && errorId ? errorId : undefined]
      .filter(Boolean)
      .join(" ");
    return (
      <div className="space-y-1">
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-fg">
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedByIds || undefined}
          className={cn(
            // min-w-0 is mandatory so the input can shrink inside flex
            // parents (per docs/design-canon/01-ui/layout.md R1). Without
            // it, the input's intrinsic 20-char width inflates the parent.
            "block w-full min-w-0 rounded-soft-md border px-3 py-2 text-sm shadow-sm transition-colors",
            "placeholder:text-fg-subtle",
            "focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand",
            "disabled:bg-surface-muted disabled:text-fg-subtle",
            error
              ? "border-danger-border-strong focus:border-danger-solid focus:ring-danger-solid"
              : "border-strong",
            className,
          )}
          {...props}
        />
        {error && (
          <p id={errorId} role="alert" className="text-xs text-danger-solid">
            {error}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";
